import { db } from "../firebase";
import {
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  addDoc,
  deleteDoc,
  collection,
  writeBatch,
  documentId,
} from "firebase/firestore";
import { quyTacMaChuVuonApi } from "./quyTacMaChuVuon";
import { langNgheFirestore } from "../lib/langNgheFirestore";
import { chuanHoaMaTimKiem } from "../lib/chuanHoaMaTimKiem";
import { kiemTraThongTinCaNhanChuVuon } from "../lib/thongTinCaNhanChuVuon";
import {
  chuanHoaCccd,
  hashCccd,
  maskCccd,
  coVeLaCccd,
  coPepperCccd,
  soCccdDayDuTuHoSo,
  soCccdTuDuLieuTho,
  soCccdDayDuAsync,
} from "../lib/cccdBaoMat";

const chuVuonCol = () => collection(db, "chuVuon");

const BATCH_TOI_DA = 400;

/**
 * Field `cccd` trên object trả về = mask (UI nhân viên).
 * `cccdDayDu` = số đủ (field plaintext hoặc doc id cũ = CCCD) — dùng chứng từ thuế.
 */
function mapDocChuVuon(d) {
  const id = d.id;
  const data =
    typeof d.data === "function"
      ? d.data()
      : (() => {
          const { id: _boId, ...rest } = d;
          return rest;
        })();
  const thô = data ?? {};
  const { cccd: _plaintextLegacy, ...rest } = thô;
  const cccdDayDu = soCccdTuDuLieuTho(id, thô);
  const cccdMask =
    (rest.cccdMask && String(rest.cccdMask)) || (cccdDayDu ? maskCccd(cccdDayDu) : "") || "";
  return {
    id,
    ...rest,
    cccdMask: cccdMask || null,
    cccd: cccdMask,
    cccdDayDu: cccdDayDu || null,
  };
}

function mapDanhSachTho(raw) {
  if (!raw) return raw;
  if (Array.isArray(raw)) return raw.map((row) => mapDocChuVuon(row));
  return mapDocChuVuon(raw);
}

/** Exact Firestore trước; nếu không ra (vd gõ thiếu gạch) thì quét + chuẩn hoá. */
async function timTheoMaLinhHoat(maNhap) {
  const raw = String(maNhap ?? "").trim();
  if (!raw) return [];

  const bienThe = [...new Set([raw, raw.toUpperCase(), raw.toLowerCase()])];
  const daThay = new Set();
  const ketQua = [];

  const snaps = await Promise.all(
    bienThe.map((v) => getDocs(query(chuVuonCol(), where("maChuVuon", "==", v))))
  );
  for (const snap of snaps) {
    for (const d of snap.docs) {
      if (!daThay.has(d.id)) {
        daThay.add(d.id);
        ketQua.push(mapDocChuVuon(d));
      }
    }
  }
  if (ketQua.length > 0) return ketQua;

  const chuan = chuanHoaMaTimKiem(raw);
  if (!chuan) return [];

  const all = await getDocs(chuVuonCol());
  return all.docs.map(mapDocChuVuon).filter((cv) => chuanHoaMaTimKiem(cv.maChuVuon) === chuan);
}

async function ghiBatch(ops) {
  for (let i = 0; i < ops.length; i += BATCH_TOI_DA) {
    const batch = writeBatch(db);
    for (const op of ops.slice(i, i + BATCH_TOI_DA)) op(batch);
    await batch.commit();
  }
}

/**
 * Đổi FK maChuVuon (doc id chủ vườn) trên vuon / giaoDich / banNhapThuMua
 * và targetId trên yeuCauSuaChuVuonVuon (+ lock pending).
 */
async function capNhatFkChuVuon(oldId, newId) {
  const [vuonSnap, gdSnap, nhapSnap, ycSnap] = await Promise.all([
    getDocs(query(collection(db, "vuon"), where("maChuVuon", "==", oldId))),
    getDocs(query(collection(db, "giaoDich"), where("maChuVuon", "==", oldId))),
    getDocs(query(collection(db, "banNhapThuMua"), where("maChuVuon", "==", oldId))),
    getDocs(
      query(
        collection(db, "yeuCauSuaChuVuonVuon"),
        where("loai", "==", "chuVuon"),
        where("targetId", "==", oldId)
      )
    ),
  ]);

  const ops = [];
  for (const d of vuonSnap.docs) {
    ops.push((batch) => batch.update(d.ref, { maChuVuon: newId }));
  }
  for (const d of gdSnap.docs) {
    ops.push((batch) => batch.update(d.ref, { maChuVuon: newId }));
  }
  for (const d of nhapSnap.docs) {
    const data = d.data();
    const maVuon = data.maVuon;
    const newRef = doc(db, "banNhapThuMua", `${newId}_${maVuon}`);
    ops.push((batch) => {
      batch.set(newRef, { ...data, maChuVuon: newId });
      batch.delete(d.ref);
    });
  }
  for (const d of ycSnap.docs) {
    ops.push((batch) => batch.update(d.ref, { targetId: newId }));
  }

  // Lock pending: id = chuVuon__{targetId} — xoá cũ, tạo mới nếu còn tồn tại.
  const lockCu = doc(db, "yeuCauSuaPendingLock", `chuVuon__${oldId}`);
  const lockSnap = await getDoc(lockCu);
  if (lockSnap.exists()) {
    const lockData = lockSnap.data();
    const lockMoi = doc(db, "yeuCauSuaPendingLock", `chuVuon__${newId}`);
    ops.push((batch) => {
      batch.set(lockMoi, { ...lockData, targetId: newId });
      batch.delete(lockCu);
    });
  }

  await ghiBatch(ops);

  return {
    vuon: vuonSnap.size,
    giaoDich: gdSnap.size,
    banNhapThuMua: nhapSnap.size,
    yeuCauSua: ycSnap.size,
    pendingLock: lockSnap.exists() ? 1 : 0,
  };
}

export const chuVuonApi = {
  async danhSachTatCa() {
    const snap = await getDocs(chuVuonCol());
    return snap.docs.map(mapDocChuVuon);
  },

  langNgheDanhSach(onChange, onError) {
    return langNgheFirestore(chuVuonCol(), {
      mapAsync: mapDanhSachTho,
      onChange,
      onError,
    });
  },

  langNgheChiTiet(id, onChange, onError) {
    if (!id) {
      onChange(null);
      return () => {};
    }
    return langNgheFirestore(doc(db, "chuVuon", id), {
      mapAsync: mapDanhSachTho,
      onChange,
      onError,
    });
  },

  // Tra theo CCCD (hash / legacy plaintext), SĐT hoặc mã chủ vườn.
  async timKiem(q) {
    if (!q) return [];
    const tuKhoa = String(q).trim();
    if (!tuKhoa) return [];

    const digits = chuanHoaCccd(tuKhoa);
    const promises = [
      getDocs(query(chuVuonCol(), where("sdt", "==", tuKhoa))),
      timTheoMaLinhHoat(tuKhoa),
    ];

    if (coVeLaCccd(tuKhoa)) {
      const hash = await hashCccd(digits);
      promises.push(getDocs(query(chuVuonCol(), where("cccdHash", "==", hash))));
      // Transition: doc cũ còn field plaintext `cccd`.
      promises.push(getDocs(query(chuVuonCol(), where("cccd", "==", digits))));
      if (tuKhoa !== digits) {
        promises.push(getDocs(query(chuVuonCol(), where("cccd", "==", tuKhoa))));
      }
    }

    const ketQuaTho = await Promise.all(promises);
    const daThay = new Set();
    const ketQua = [];
    for (const part of ketQuaTho) {
      const rows = Array.isArray(part) ? part : part.docs.map(mapDocChuVuon);
      for (const row of rows) {
        if (!daThay.has(row.id)) {
          daThay.add(row.id);
          ketQua.push(row);
        }
      }
    }
    return ketQua;
  },

  // Dùng khi quét QR mã chủ vườn (xem QrCodeImage trong quan-ly-chu-vuon-vuon).
  async timTheoMa(maChuVuon) {
    return timTheoMaLinhHoat(maChuVuon);
  },

  async chiTiet(id) {
    const snap = await getDoc(doc(db, "chuVuon", id));
    if (!snap.exists()) throw new Error("Không tìm thấy chủ vườn");
    return mapDocChuVuon(snap);
  },

  /** Lấy hồ sơ theo doc id, không có thì theo mã chủ vườn. */
  async taiHoSo(id) {
    if (!id) return null;
    const snap = await getDoc(doc(db, "chuVuon", id));
    if (snap.exists()) return mapDocChuVuon(snap);
    const theoMa = await timTheoMaLinhHoat(id);
    return theoMa[0] ?? null;
  },

  async ganCccdThue(cv) {
    if (!cv) return null;
    const so = await soCccdDayDuAsync(cv);
    return { ...cv, cccdDayDu: so || cv.cccdDayDu || null };
  },

  /**
   * Batch lấy hồ sơ chủ vườn cho Biểu 01 (chứng từ thu mua).
   * documentId() `in` (30/lần) thay vì N getDoc; chỉ brute-force HMAC khi thiếu plaintext.
   * @returns {Object<string, object>} maChuVuon (doc id) → hồ sơ có cccdDayDu.
   */
  async taiHoSoChoChungTu(ids) {
    const unique = [...new Set((ids ?? []).filter(Boolean))];
    if (unique.length === 0) return {};

    const result = {};
    const CHUNK = 30;
    for (let i = 0; i < unique.length; i += CHUNK) {
      const chunk = unique.slice(i, i + CHUNK);
      const snap = await getDocs(query(chuVuonCol(), where(documentId(), "in", chunk)));
      for (const d of snap.docs) {
        result[d.id] = mapDocChuVuon(d);
      }
    }

    const thieu = unique.filter((id) => !result[id]);
    await Promise.all(
      thieu.map(async (id) => {
        const cv = await this.taiHoSo(id);
        if (cv) result[cv.id] = cv;
      })
    );

    const canAsync = [];
    for (const [id, cv] of Object.entries(result)) {
      const nhanh = soCccdDayDuTuHoSo(cv);
      if (nhanh) {
        result[id] = { ...cv, cccdDayDu: nhanh };
      } else {
        canAsync.push({ id, cv });
      }
    }
    await Promise.all(
      canAsync.map(async ({ id, cv }) => {
        const so = await soCccdDayDuAsync(cv);
        result[id] = { ...cv, cccdDayDu: so || cv.cccdDayDu || null };
      })
    );

    return result;
  },

  /**
   * Tạo chủ vườn mới: auto Firestore ID (không dùng CCCD làm doc id).
   * Lưu số CCCD đủ (chứng từ thuế) + hash/mask (tìm kiếm / UI nhân viên).
   */
  async taoMoi({ hoTen, cccd, sdt, loaiChuVuon, tinhMa, xaMa, ...rest }) {
    const daKiem = kiemTraThongTinCaNhanChuVuon({ hoTen, cccd, sdt, tinhMa, xaMa, ...rest });
    const digits = chuanHoaCccd(daKiem.cccd);
    const cccdHashVal = await hashCccd(digits);
    const cccdMaskVal = maskCccd(digits);

    const trungHash = await getDocs(query(chuVuonCol(), where("cccdHash", "==", cccdHashVal)));
    if (!trungHash.empty) {
      throw new Error("Số CCCD này đã tồn tại trong hệ thống");
    }
    const trungLegacy = await getDocs(query(chuVuonCol(), where("cccd", "==", digits)));
    if (!trungLegacy.empty) {
      throw new Error("Số CCCD này đã tồn tại trong hệ thống");
    }
    const legacyIdSnap = await getDoc(doc(db, "chuVuon", digits));
    if (legacyIdSnap.exists()) {
      throw new Error("Số CCCD này đã tồn tại trong hệ thống");
    }

    const loai = loaiChuVuon || "thuong";
    const maChuVuon = await quyTacMaChuVuonApi.sinhMa({ loaiChuVuon: loai, tinhMa, xaMa });
    const { cccd: _boCccd, ...restKhongCccd } = rest;
    const data = {
      hoTen,
      sdt,
      loaiChuVuon: loai,
      tinhMa: tinhMa || "",
      xaMa: xaMa || "",
      maChuVuon,
      ...restKhongCccd,
      noiCapCccd: daKiem.noiCapCccd,
      cccd: digits,
      cccdHash: cccdHashVal,
      cccdMask: cccdMaskVal,
      createdAt: new Date().toISOString(),
    };
    const ref = await addDoc(chuVuonCol(), data);
    return mapDocChuVuon({ id: ref.id, data: () => data });
  },

  /**
   * Cập nhật hồ sơ. Không đổi CCCD đã có.
   * Hồ sơ thiếu số đủ: admin được điền lại (hash phải khớp nếu đã có hash).
   */
  async capNhat(id, data) {
    const payload = { ...(data ?? {}) };
    const cccdNhap = chuanHoaCccd(payload.cccd);
    delete payload.cccd;
    delete payload.cccdHash;
    delete payload.cccdMask;
    delete payload.cccdDayDu;
    delete payload.id;

    const hienTai = await chuVuonApi.chiTiet(id);
    const daCoSoDu = soCccdDayDuTuHoSo(hienTai);
    if (!daCoSoDu && cccdNhap.length >= 9) {
      const hashMoi = await hashCccd(cccdNhap);
      if (hienTai.cccdHash && hienTai.cccdHash !== hashMoi) {
        throw new Error("Số CCCD không khớp hồ sơ đã lưu trên hệ thống.");
      }
      payload.cccd = cccdNhap;
      payload.cccdMask = maskCccd(cccdNhap);
      if (!hienTai.cccdHash) payload.cccdHash = hashMoi;
    }

    if (Object.keys(payload).length === 0) {
      return chuVuonApi.chiTiet(id);
    }
    await updateDoc(doc(db, "chuVuon", id), payload);
    return chuVuonApi.chiTiet(id);
  },

  /**
   * Thêm hash+mask, đổi doc id nếu id === CCCD. Giữ số CCCD đủ (cần chứng từ thuế).
   */
  async migrateCccdBaoMat({ onProgress } = {}) {
    if (!coPepperCccd()) {
      throw new Error(
        "Thiếu REACT_APP_CCCD_PEPPER trong bundle — thêm vào frontend/.env, restart npm start (dev) hoặc build+deploy hosting (prod), rồi thử lại. Không commit giá trị thật."
      );
    }
    const snap = await getDocs(chuVuonCol());
    const tong = snap.size;
    let daXuLy = 0;
    let capNhatTaiCho = 0;
    let rewriteId = 0;
    let boQua = 0;
    let loi = 0;
    const chiTietLoi = [];

    for (const d of snap.docs) {
      const data = d.data() ?? {};
      const tuField = data.cccd != null ? chuanHoaCccd(data.cccd) : "";
      const tuId = /^\d{9,12}$/.test(d.id) ? d.id : "";
      const plaintext = tuField || tuId;
      const thieuHash = !data.cccdHash;
      const thieuMask = !data.cccdMask;
      const idLaCccd = Boolean(plaintext) && d.id === plaintext;

      if (!thieuHash && !thieuMask && tuField && !idLaCccd) {
        boQua += 1;
        daXuLy += 1;
        onProgress?.({ daXuLy, tong, boQua, capNhatTaiCho, rewriteId, loi });
        continue;
      }

      if (!plaintext) {
        if (thieuHash) {
          loi += 1;
          chiTietLoi.push({ id: d.id, lyDo: "Thiếu số CCCD đủ để tính hash / chứng từ thuế" });
        } else {
          boQua += 1;
        }
        daXuLy += 1;
        onProgress?.({ daXuLy, tong, boQua, capNhatTaiCho, rewriteId, loi });
        continue;
      }

      try {
        const cccdHashVal = await hashCccd(plaintext);
        const cccdMaskVal = maskCccd(plaintext);

        if (idLaCccd) {
          const trungHash = await getDocs(
            query(chuVuonCol(), where("cccdHash", "==", cccdHashVal))
          );
          const daCo = trungHash.docs.find((x) => x.id !== d.id);
          let newId;
          if (daCo) {
            newId = daCo.id;
          } else {
            const newData = {
              ...data,
              cccd: plaintext,
              cccdHash: cccdHashVal,
              cccdMask: cccdMaskVal,
            };
            const newRef = await addDoc(chuVuonCol(), newData);
            newId = newRef.id;
          }
          await capNhatFkChuVuon(d.id, newId);
          await deleteDoc(d.ref);
          rewriteId += 1;
        } else {
          await updateDoc(d.ref, {
            cccd: plaintext,
            cccdHash: data.cccdHash || cccdHashVal,
            cccdMask: data.cccdMask || cccdMaskVal,
          });
          if (data.cccdHash && data.cccdHash !== cccdHashVal) {
            chiTietLoi.push({
              id: d.id,
              lyDo: "cccdHash sẵn có khác hash tính lại (pepper đổi?) — giữ hash cũ, đã giữ số đủ",
            });
          }
          capNhatTaiCho += 1;
        }
      } catch (e) {
        loi += 1;
        chiTietLoi.push({ id: d.id, lyDo: e?.message || String(e) });
      }

      daXuLy += 1;
      onProgress?.({ daXuLy, tong, boQua, capNhatTaiCho, rewriteId, loi });
    }

    return {
      tong,
      daXuLy,
      capNhatTaiCho,
      rewriteId,
      boQua,
      loi,
      chiTietLoi,
    };
  },
};
